import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import strategyLevelService from '../services/strategyLevelService'
import StrategyLevelBadge from '../components/StrategyLevelBadge'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  ArrowLeft,
  TrendingUp,
  Target,
  Award,
  Calendar,
  Trophy,
  Zap,
  Clock,
  BarChart3,
  Calculator,
  BookOpen
} from 'lucide-react'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('StrategyProgress')

const StrategyProgress = () => {
  const { strategyType } = useParams()
  const navigate = useNavigate()
  const { user, getStrategyLevel } = useAuthStore()
  const [strategyLevel, setStrategyLevel] = useState(null)
  const [history, setHistory] = useState([])
  const [leaderboard, setLeaderboard] = useState([])
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [leaderboardLoading, setLeaderboardLoading] = useState(false)

  useEffect(() => {
    if (strategyType) {
      loadStrategyData()
    }
  }, [strategyType])

  const loadStrategyData = async () => {
    try {
      setLoading(true)

      // Fetch strategy level from store first
      const cachedLevel = getStrategyLevel(strategyType)
      if (cachedLevel) {
        setStrategyLevel(cachedLevel)
      }

      // Fetch fresh data from API
      const level = await strategyLevelService.getStrategyLevel(strategyType)
      if (level) {
        setStrategyLevel(level)
      } else {
        toast.error('Strategy level not found')
        navigate('/profile')
        return
      }

      // Load history and leaderboard in parallel
      loadHistory()
      loadLeaderboard()
    } catch (error) {
      logger.error('Error loading strategy data:', error)
      toast.error('Failed to load strategy data')
    } finally {
      setLoading(false)
    }
  }

  const loadHistory = async () => {
    try {
      setHistoryLoading(true)
      const historyData = await strategyLevelService.getStrategyHistory(strategyType, {
        limit: 10,
        sortOrder: 'DESC'
      })
      setHistory(historyData || [])
    } catch (error) {
      logger.error('Error loading history:', error)
    } finally {
      setHistoryLoading(false)
    }
  }

  const loadLeaderboard = async () => {
    try {
      setLeaderboardLoading(true)
      const leaderboardData = await strategyLevelService.getLeaderboard(strategyType, {
        limit: 10
      })
      setLeaderboard(leaderboardData || [])
    } catch (error) {
      logger.error('Error loading leaderboard:', error)
    } finally {
      setLeaderboardLoading(false)
    }
  }

  const formatDate = (date) => {
    if (!date) return 'Never'
    const d = new Date(date)
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDateRelative = (date) => {
    if (!date) return 'Never'
    const d = new Date(date)
    const now = new Date()
    const diff = now - d
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0) return 'Today'
    if (days === 1) return 'Yesterday'
    if (days < 7) return `${days} days ago`
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`
    return d.toLocaleDateString()
  }

  if (loading || !strategyLevel) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const displayName = strategyLevelService.getStrategyDisplayName(strategyType)
  const isMath = strategyType === 'SAT_MATH' || strategyType === 'SAT_MATH_MINI'
  const StrategyIcon = isMath ? Calculator : BookOpen
  const iconBg = isMath
    ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
    : 'bg-gradient-to-br from-emerald-500 to-green-600'

  const {
    currentLevel,
    highestLevel,
    startingLevel,
    averageScore,
    bestScore,
    totalChallenges,
    completedChallenges,
    lastChallengeAt
  } = strategyLevel

  const levelsGained = currentLevel - startingLevel
  const progressToMastery = currentLevel ? Math.round((currentLevel / 10) * 100) : 0
  const isAtPeak = currentLevel === highestLevel
  const scoreDifference = bestScore - averageScore

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="card">
        <div className="card-body">
          <button
            onClick={() => navigate(-1)}
            className="btn-secondary flex items-center space-x-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div
                className={`flex items-center justify-center w-14 h-14 rounded-2xl ${iconBg} text-white flex-shrink-0`}
              >
                <StrategyIcon className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {displayName}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {completedChallenges} challenge{completedChallenges !== 1 ? 's' : ''} completed
                  {levelsGained > 0 &&
                    ` • Gained ${levelsGained} level${levelsGained !== 1 ? 's' : ''}`}
                </p>
              </div>
            </div>
            <StrategyLevelBadge level={currentLevel} size="large" animated />
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body">
            <p className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <Award className="w-3.5 h-3.5" />
              Current Level
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{currentLevel}</p>
            {isAtPeak && highestLevel > startingLevel && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 mt-1">
                <Trophy className="w-3 h-3" />
                Peak Level
              </span>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <p className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <TrendingUp className="w-3.5 h-3.5" />
              Average Score
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {averageScore?.toFixed(1) || 0}%
            </p>
            {scoreDifference > 10 && (
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Best: {bestScore?.toFixed(0)}%
              </span>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <p className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <Target className="w-3.5 h-3.5" />
              Best Score
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {bestScore?.toFixed(0) || 0}%
            </p>
            {scoreDifference > 20 && (
              <span className="inline-flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 mt-1">
                <Zap className="w-3 h-3" />
                High Potential
              </span>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <p className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <Calendar className="w-3.5 h-3.5" />
              Last Challenge
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {formatDateRelative(lastChallengeAt)}
            </p>
            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {formatDate(lastChallengeAt)}
            </span>
          </div>
        </div>
      </div>

      {/* Progress to Mastery */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Progress to Mastery
          </h3>
        </div>
        <div className="card-body">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Level {currentLevel} of 10
            </span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {progressToMastery}%
            </span>
          </div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary-500 to-purple-600 transition-all duration-500"
              style={{ width: `${progressToMastery}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span>Started at Level {startingLevel}</span>
            {highestLevel > currentLevel && <span>Peak: Level {highestLevel}</span>}
          </div>
        </div>
      </div>

      {/* Level History */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Level History
            </h3>
            {history.length > 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {history.length} record{history.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <div className="card-body">
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="sm" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No level changes yet</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                Complete more challenges to build your history
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((record, index) => (
                <div
                  key={record.id}
                  className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <div className="flex-shrink-0">
                    <StrategyLevelBadge level={record.newLevel} size="small" showLabel={false} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        Level {record.oldLevel} → {record.newLevel}
                      </span>
                      {record.changeType === 'PROMOTION' ? (
                        <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded">
                          Promoted
                        </span>
                      ) : record.changeType === 'DEMOTION' ? (
                        <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 px-2 py-0.5 rounded">
                          Demoted
                        </span>
                      ) : null}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{record.reason}</p>
                    {record.domainScores && Object.keys(record.domainScores).length > 0 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Domain Scores:{' '}
                        {Object.entries(record.domainScores)
                          .map(([domain, score]) => `${domain}: ${score}%`)
                          .join(', ')}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {formatDate(record.createdAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Leaderboard */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Leaderboard
            </h3>
          </div>
        </div>
        <div className="card-body">
          {leaderboardLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="sm" />
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-8">
              <BarChart3 className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No leaderboard data yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                      Rank
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                      User
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                      Level
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                      Avg Score
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                      Challenges
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((entry, index) => {
                    const isCurrentUser = entry.userId === user?.id
                    return (
                      <tr
                        key={entry.userId}
                        className={`border-b border-gray-100 dark:border-gray-800 ${
                          isCurrentUser ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                        }`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {index < 3 ? (
                              <Trophy
                                className={`w-5 h-5 ${
                                  index === 0
                                    ? 'text-yellow-500'
                                    : index === 1
                                      ? 'text-gray-400'
                                      : 'text-amber-600'
                                }`}
                              />
                            ) : (
                              <span className="text-gray-600 dark:text-gray-400 font-medium">
                                {index + 1}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {entry.user?.publicName || 'Anonymous'}
                            {isCurrentUser && (
                              <span className="ml-2 text-xs text-primary-600 dark:text-primary-400">
                                (You)
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <StrategyLevelBadge
                            level={entry.currentLevel}
                            size="small"
                            showLabel={false}
                          />
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-gray-900 dark:text-gray-100">
                            {entry.averageScore?.toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-gray-600 dark:text-gray-400">
                            {entry.completedChallenges}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default StrategyProgress
